/**
 * DRM Management — LIVE key-rotation view model.
 *
 * sig-drm rotates LIVE keys PER PERIOD:
 *   period = floor(unix / period_secs)
 *   key    = HKDF(master, streamID ‖ period)
 * The player fetches each period's key from a deterministic SIGNED URL, so DVR
 * rewind re-fetches reproducible old-period keys.
 *
 * There is no rotation timer to poll — rotation is a pure function of the wall
 * clock + period_secs. The console derives the current period id and the
 * countdown locally, and renders a synthetic period timeline. Backed by stream
 * fields (period_secs, dvr_seconds) from sig-control. No new endpoint required.
 *
 * Helper math only — kept framework-free so the live view + timeline can share it.
 */

export interface RotationState {
  periodSecs: number;
  /** floor(unix / periodSecs) — the period id the live edge is keyed with. */
  currentPeriod: number;
  /** unix seconds when the current period started. */
  periodStart: number;
  /** unix seconds when the next rotation happens. */
  nextRotation: number;
  /** seconds remaining until next rotation (>= 0). */
  secondsToNext: number;
  /** 0..1 progress through the current period (for a progress bar). */
  progress: number;
}

/** Derive the live rotation state for a stream at a given moment. */
export function rotationStateAt(periodSecs: number, nowMs: number = Date.now()): RotationState {
  const now = Math.floor(nowMs / 1000);
  const currentPeriod = Math.floor(now / periodSecs);
  const periodStart = currentPeriod * periodSecs;
  const nextRotation = periodStart + periodSecs;
  const secondsToNext = Math.max(0, nextRotation - now);
  const progress = periodSecs > 0 ? (now - periodStart) / periodSecs : 0;
  return { periodSecs, currentPeriod, periodStart, nextRotation, secondsToNext, progress };
}

export interface RotationPeriodEntry {
  period: number; // period id
  startsAt: number; // unix seconds
  endsAt: number; // unix seconds
  inDvrWindow: boolean; // still re-fetchable for DVR rewind
  isCurrent: boolean; // the live edge
}

/**
 * Build the recent period timeline (newest first) for the rotation history view.
 * `count` periods back from now; flags which are still inside the DVR window
 * (i.e. old-period keys a rewinding player can still reproduce).
 */
export function recentPeriods(
  periodSecs: number,
  dvrSeconds: number,
  count = 12,
  nowMs: number = Date.now(),
): RotationPeriodEntry[] {
  const st = rotationStateAt(periodSecs, nowMs);
  const now = Math.floor(nowMs / 1000);
  const dvrFloor = now - dvrSeconds;
  const out: RotationPeriodEntry[] = [];
  for (let i = 0; i < count; i += 1) {
    const period = st.currentPeriod - i;
    const startsAt = period * periodSecs;
    const endsAt = startsAt + periodSecs;
    out.push({
      period,
      startsAt,
      endsAt,
      inDvrWindow: endsAt >= dvrFloor,
      isCurrent: i === 0,
    });
  }
  return out;
}
